import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, endOfMonth } from 'date-fns';
import { useClientFilter } from '@/hooks/useClientFilter';

interface ProductivityMetrics {
  avgResolutionHours: number;
  avgFirstResponseHours: number;
  resolutionRate: number;
  sameDay: number;
  oneToThreeDays: number;
  threeToSevenDays: number;
  moreThanSevenDays: number;
  backlogChange: number;
}

interface TeamPerformance {
  id: string;
  name: string;
  totalTickets: number;
  resolvedTickets: number;
  resolutionRate: number;
  avgResolutionHours: number;
}

interface PriorityDistribution {
  baixa: number;
  media: number;
  alta: number;
  critica: number;
}

interface ClientMetrics {
  id: string;
  name: string;
  totalTickets: number;
  resolvedTickets: number;
  resolutionRate: number;
}

interface TrendComparison {
  currentPeriod: {
    total: number;
    resolved: number;
    resolutionRate: number;
    avgSLA: number;
  };
  previousPeriod: {
    total: number;
    resolved: number;
    resolutionRate: number;
    avgSLA: number;
  };
  changes: {
    totalChange: number;
    resolvedChange: number;
    resolutionRateChange: number;
    slaChange: number;
  };
}

export const useAdvancedReportsData = (selectedMonth: Date) => {
  const startDate = startOfMonth(selectedMonth);
  const endDate = endOfMonth(selectedMonth);
  const { getClientFilter } = useClientFilter();

  const withClient = (query: any, field = 'client_id') => {
    const clientId = getClientFilter();
    return clientId ? query.eq(field, clientId) : query;
  };

  // Productivity Metrics
  const { data: productivityMetrics, isLoading: productivityLoading } = useQuery({
    queryKey: ['productivity-metrics', startDate, endDate, getClientFilter()],
    queryFn: async (): Promise<ProductivityMetrics> => {
      const { data: solicitacoesData } = await withClient(
        supabase
          .from('solicitacoes')
          .select('criado_em, data_resolucao, status')
          .gte('criado_em', startDate.toISOString())
          .lte('criado_em', endDate.toISOString())
      );

      const allTickets = solicitacoesData || [];
      const totalCreated = allTickets.length;
      const resolvedTickets = allTickets.filter(t => 
        t.status === 'resolvida' || t.status === 'fechada'
      );

      const resolutionTimes = resolvedTickets
        .filter(t => t.data_resolucao)
        .map(t => {
          return (new Date(t.data_resolucao!).getTime() - new Date(t.criado_em).getTime()) / (1000 * 60 * 60);
        });

      const avgResolutionHours = resolutionTimes.length > 0
        ? resolutionTimes.reduce((a, b) => a + b, 0) / resolutionTimes.length
        : 0;

      const sameDay = resolutionTimes.filter(h => h <= 24).length;
      const oneToThreeDays = resolutionTimes.filter(h => h > 24 && h <= 72).length;
      const threeToSevenDays = resolutionTimes.filter(h => h > 72 && h <= 168).length;
      const moreThanSevenDays = resolutionTimes.filter(h => h > 168).length;

      const resolutionRate = totalCreated > 0
        ? (resolvedTickets.length / totalCreated) * 100
        : 0;

      const backlogChange = resolvedTickets.length - totalCreated;

      return {
        avgResolutionHours,
        avgFirstResponseHours: 0,
        resolutionRate,
        sameDay,
        oneToThreeDays,
        threeToSevenDays,
        moreThanSevenDays,
        backlogChange,
      };
    },
    refetchInterval: 60000,
  });

  // Team Performance
  const { data: teamPerformance, isLoading: teamLoading } = useQuery({
    queryKey: ['team-performance', startDate, endDate, getClientFilter()],
    queryFn: async (): Promise<TeamPerformance[]> => {
      const { data: tickets } = await withClient(
        supabase
          .from('solicitacoes')
          .select('atendente_id, criado_em, data_resolucao, status')
          .gte('criado_em', startDate.toISOString())
          .lte('criado_em', endDate.toISOString())
          .not('atendente_id', 'is', null)
      );

      if (!tickets || tickets.length === 0) return [];

      const userIds = [...new Set(tickets.map(t => t.atendente_id).filter(Boolean))] as string[];

      const { data: users } = await supabase
        .from('users')
        .select('id, name')
        .in('id', userIds);

      const userMetrics = users?.map(user => {
        const userTickets = tickets.filter(t => t.atendente_id === user.id);
        const resolvedTickets = userTickets.filter(t => 
          t.status === 'resolvida' || t.status === 'fechada'
        );

        const resolutionTimes = resolvedTickets
          .filter(t => t.data_resolucao)
          .map(t => 
            (new Date(t.data_resolucao).getTime() - new Date(t.criado_em).getTime()) / (1000 * 60 * 60)
          );

        const avgResolutionHours = resolutionTimes.length > 0
          ? resolutionTimes.reduce((a, b) => a + b, 0) / resolutionTimes.length
          : 0;

        return {
          id: user.id,
          name: user.name,
          totalTickets: userTickets.length,
          resolvedTickets: resolvedTickets.length,
          resolutionRate: userTickets.length > 0
            ? (resolvedTickets.length / userTickets.length) * 100
            : 0,
          avgResolutionHours,
        };
      }) || [];

      return userMetrics.sort((a, b) => b.resolvedTickets - a.resolvedTickets).slice(0, 5);
    },
    refetchInterval: 60000,
  });

  // Priority Distribution
  const { data: priorityDistribution, isLoading: priorityLoading } = useQuery({
    queryKey: ['priority-distribution', startDate, endDate, getClientFilter()],
    queryFn: async (): Promise<PriorityDistribution> => {
      const { data: solicitacoesData } = await withClient(
        supabase
          .from('solicitacoes')
          .select('prioridade')
          .gte('criado_em', startDate.toISOString())
          .lte('criado_em', endDate.toISOString())
      );

      const allTickets = solicitacoesData || [];

      return {
        baixa: allTickets.filter(t => t.prioridade === 'baixa').length,
        media: allTickets.filter(t => t.prioridade === 'media').length,
        alta: allTickets.filter(t => t.prioridade === 'alta').length,
        critica: allTickets.filter(t => t.prioridade === 'critica').length,
      };
    },
    refetchInterval: 60000,
  });

  // Client Analysis
  const { data: clientMetrics, isLoading: clientLoading } = useQuery({
    queryKey: ['client-metrics', startDate, endDate, getClientFilter()],
    queryFn: async (): Promise<ClientMetrics[]> => {
      const { data: tickets } = await withClient(
        supabase
          .from('solicitacoes')
          .select('client_id, status')
          .gte('criado_em', startDate.toISOString())
          .lte('criado_em', endDate.toISOString())
          .not('client_id', 'is', null)
      );

      if (!tickets || tickets.length === 0) return [];

      const clientIds = [...new Set(tickets.map(t => t.client_id).filter(Boolean))] as string[];

      const { data: clients } = await supabase
        .from('clients')
        .select('id, name')
        .in('id', clientIds);

      const clientData = clients?.map(client => {
        const clientTickets = tickets.filter(t => t.client_id === client.id);
        const resolvedTickets = clientTickets.filter(t => 
          t.status === 'resolvida' || t.status === 'fechada'
        );

        return {
          id: client.id,
          name: client.name,
          totalTickets: clientTickets.length,
          resolvedTickets: resolvedTickets.length,
          resolutionRate: clientTickets.length > 0
            ? (resolvedTickets.length / clientTickets.length) * 100
            : 0,
        };
      }) || [];

      return clientData.sort((a, b) => b.totalTickets - a.totalTickets).slice(0, 5);
    },
    refetchInterval: 60000,
  });

  return {
    productivityMetrics,
    productivityLoading,
    teamPerformance,
    teamLoading,
    priorityDistribution,
    priorityLoading,
    clientMetrics,
    clientLoading,
    isLoading: productivityLoading || teamLoading || priorityLoading || clientLoading,
  };
};
